import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MypageLayout from "./MypageLayout";
import HomeCalendar from "./HomeCalendar";
import HomeSuggestions from "./HomeSuggestions";
import TodoCard, { ACTION } from "./TodoCard";
import PunchCard from "./PunchCard";
import { WD } from "./dateUtil";
import { emitToast } from "./Toast";
import ErrorBar from "./ErrorBar";
import EmptyState from "./EmptyState";
import Loader from "./Loader";
import { refreshBadgeCounts } from "./notifications";
import { jsonHeaders } from "./csrf";

// 勤怠マイページ ホーム（案①・会議2026-07）。
// 構成: 今月サマリ帯（社員のみ・経営者は勤怠を登録しないため非表示）→ カレンダー（全幅）→ TODO操作カード（全幅グリッド）。
// /api/employee/home/ から本人の実データ、/api/employee/todos/ からTODOを取得。
// TODOはボードと同じ操作カード（TodoCard）を流用し、状態変更/編集/削除/メモは /api/employee/todos/ 系へ反映する。

const API_URL = process.env.REACT_APP_API_URL || "";
const ORDER = { open: 0, doing: 1, done: 2 };

// 分 → "96時間30分"（単位は小さめ表示。0分のときは「時間」のみ）
const fmtDur = (min) => {
  if (min == null) return "-";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return (<>{h}<small>時間</small>{m > 0 && <>{m}<small>分</small></>}</>);
};

const IconClipboard = () => (<svg viewBox="0 0 24 24"><rect x="6" y="4.5" width="12" height="16.5" rx="2.4" /><path d="M9.2 4.5h5.6v2.6H9.2z" /><path d="M9 11.5h6M9 15h4" strokeLinecap="round" /></svg>);
const IconChevron = () => (<svg viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" /></svg>);

function greeting(name) {
  const h = new Date().getHours();
  const g = h < 11 ? "おはようございます" : h < 18 ? "こんにちは" : "こんばんは";
  return name ? `${g}、${name}さん` : g;
}
function todayLabel() {
  const d = new Date();
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日 ${WD[d.getDay()]}曜日`;
}

function MypageHome() {
  const [data, setData] = useState(null);
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // TODO取得（操作カード用に assignee/memos を補う）。変更後の再取得にも使う。失敗は握りつぶす。
  // ホームは未対応・対応中しか表示しないので、サーバ側で絞って取る。全件取ると
  // 開くたびに「これまで完了した全TODO」を落として下の filter で捨てることになる。
  const refreshTodos = useCallback(() => {
    fetch(`${API_URL}/api/employee/todos/?status=open,doing`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setTodos((d.results || []).map((t) => ({ assignee: null, memos: [], ...t }))); })
      .catch(() => {});
  }, []);

  // home の再取得（打刻後に当日状態＝today を更新するのに使う）。初回ロードにも流用する。
  const loadHome = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/employee/home/`, { credentials: "include" });
      if (res.status === 401) { setError("ログインが必要です。"); return; }
      if (!res.ok) throw new Error();
      const d = await res.json();
      setData(d);
    } catch (e) {
      setError("ホーム情報の取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHome();
    refreshTodos();
  }, [loadHome, refreshTodos]);

  // 打刻ボタンはページ見出し（挨拶）行の右に置く。経営者は勤怠を登録しないため出さない。
  const punchAction = data && !data.my_request?.executive
    ? <PunchCard today={data.today} onDone={loadHome} />
    : null;

  return (
    <MypageLayout active="home" subtitle={todayLabel()} title={greeting(data?.user?.name)} actions={punchAction}>
      {loading && <Loader />}
      {error && <ErrorBar message={error} onClose={() => setError("")} />}
      {data && <HomeBody d={data} todos={todos} refreshTodos={refreshTodos} />}
    </MypageLayout>
  );
}

function HomeBody({ d, todos, refreshTodos }) {
  // home API が my_request / user / approvals を欠いて返しても、ホーム全体が例外で
  // 白画面にならないよう、参照前に安全な既定へ落とす（ここは入口＝落ちると全滅するため特に厳格に）。
  const mr = d.my_request || {};
  const ms = d.month_summary || {};
  const user = d.user || {};
  const ap = mr.approvals || {};
  // 承認待ちアラート：経営者＝承認すべき件数／社員＝自分の申請中。0件なら帯を出さない。
  const isExec = mr.executive;
  const approvalCount = isExec
    ? (ap.leave || 0) + (ap.correction || 0) + (ap.overtime || 0)
    : (mr.pending_count || 0);
  const approvalLabel = isExec ? "承認待ち" : "申請中";
  const approvalCta = isExec ? "承認する" : "申請履歴";
  const approvalTo = isExec ? "/employee/leave-approval" : "/employee/leave";
  const [openId, setOpenId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [calKey, setCalKey] = useState(0); // 承認待ちを予定化したらカレンダーを再描画するためのキー

  // 承認待ちの振り分け後：TODO化は下のTODO一覧へ、予定化はカレンダーへ即反映する。
  const onSuggestionResolved = (action) => {
    refreshTodos();
    if (action === "event") setCalKey((k) => k + 1);
  };

  // ホームの表示条件：未対応は全部＋対応中は全員分（担当を問わない）。完了は出さない。上限なし。
  const visible = todos
    .filter((t) => t.status === "open" || t.status === "doing")
    .slice()
    .sort((a, b) => ORDER[a.status] - ORDER[b.status]);

  // 変更系は BE に POST して再取得（ボードと同じAPI・サーバを唯一の真実にする）。
  const post = (path, payload) =>
    fetch(`${API_URL}${path}`, {
      method: "POST",
      credentials: "include",
      headers: jsonHeaders(),
      body: JSON.stringify(payload),
    });

  // アクション（対応する / 完了 / 完了取消）。未対応→対応中で担当者なしを自分が引き受ける（BE側で付与）。
  const advance = async (t) => {
    const a = ACTION[t.status];
    await post("/api/employee/todos/update/", { id: t.id, status: a.next });
    emitToast(a.msg);
    refreshTodos();
    // 未対応(open)を抜けた/戻った＝TODOバッジ（状態由来）も増減する。
    // TodoBoardPage の同じ操作と挙動を揃える（画面によってバッジが減ったり減らなかったりを防ぐ）。
    refreshBadgeCounts();
  };
  const startEdit = (t) => { setOpenId(null); setEditingId(t.id); };
  const cancelEdit = () => setEditingId(null);
  const saveEdit = async (id, title, due) => {
    await post("/api/employee/todos/update/", { id, title, due });
    setEditingId(null);
    refreshTodos();
  };
  const toggleMemo = (id) => setOpenId((p) => (p === id ? null : id));
  const addMemo = async (id, text) => {
    const v = (text || "").trim();
    if (!v) return;
    await post("/api/employee/todos/memo/", { id, text: v });
    refreshTodos();
  };
  const deleteMemo = async (id, mid) => {
    await post("/api/employee/todos/memo/delete/", { id, mid });
    refreshTodos();
  };
  // 削除（確認なしで即実行）。BE側が論理削除で行を残すため、誤操作は phpMyAdmin で復元できる。
  const doDelete = async (todo) => {
    const id = todo.id;
    await post("/api/employee/todos/delete/", { id });
    if (editingId === id) setEditingId(null);
    emitToast("削除しました");
    refreshTodos();
    // 未対応TODOを削除した＝TODOバッジも減る。
    refreshBadgeCounts();
  };

  return (
    <>
      {/* LINE未連携なら連携を促す。連携の効果は役割で違うので理由も出し分ける：
          社員＝打刻と通知／経営者＝打刻しないので、グループ発言の発言者を紐づけるため。
          経営者にも連携は要る（承認待ちInboxの発言者逆引きに line_user_id を使う）ので
          バナー自体は隠さない。 */}
      {!user.line_linked && (
        <div className="mp-linkbanner">
          <div className="mp-linkbanner-txt">
            <div className="mp-linkbanner-t">LINE連携がまだです</div>
            <div className="mp-linkbanner-s">
              {isExec
                ? "連携すると、グループの発言から拾った予定やTODOに発言者が表示されます。"
                : "連携すると、LINEから出退勤の打刻と通知が使えます。"}
            </div>
          </div>
          <button className="mp-btn mp-accent" onClick={() => window.open(`${API_URL}/liff/punch/link`, "_blank", "noopener")}>LINE連携</button>
        </div>
      )}

      <div className="mp-home2">
        {/* 承認待ち＝上部アラート帯（0件なら非表示）。メトリクス帯とは分離し、無いときに崩れないようにする。
            経営者＝承認すべき件数／社員＝自分の申請中の件数、で出し分け。 */}
        {approvalCount > 0 && (
          <Link className="mp-home-alert" to={approvalTo}>
            <span className="mp-home-alert-ic"><IconClipboard /></span>
            <span className="mp-home-alert-t">{approvalLabel} <b>{approvalCount}件</b></span>
            <span className="mp-home-alert-go">{approvalCta} <IconChevron /></span>
          </Link>
        )}

        {/* 今月サマリ帯（勤怠メトリクスのみ・常に整列）。
            経営者は勤怠を登録しないため、出勤日数/勤務時間/有休残の集計は意味を持たない → 社員のみ表示。 */}
        {!isExec && (
          <div className="mp-home-band">
            <div className="mp-htile">
              <span className="mp-htile-l">今月の出勤日数</span>
              <span className="mp-htile-v">{ms.work_days ?? "-"}<small>日</small></span>
            </div>
            <div className="mp-htile">
              <span className="mp-htile-l">今月の勤務時間</span>
              <span className="mp-htile-v">{fmtDur(ms.work_minutes)}</span>
            </div>
            <div className="mp-htile">
              <span className="mp-htile-l">有休残</span>
              <span className="mp-htile-v">{ms.paid_leave_days ?? "-"}<small>日</small></span>
            </div>
          </div>
        )}

        {/* カレンダー（全幅） */}
        <HomeCalendar key={calKey} />

        {/* 承認待ち（会話から拾った予定/TODO候補の振り分け・0件なら非表示）。カレンダーとTODOの間に置く。 */}
        <HomeSuggestions onResolved={onSuggestionResolved} />

        {/* TODO（未完了の上位＋操作カード） */}
        <div className="mp-home-todo">
          <div className="mp-rhead"><h2>TODO</h2><Link to="/employee/todo">すべて見る</Link></div>
          {visible.length === 0 ? (
            <EmptyState bare>TODOはありません</EmptyState>
          ) : (
            <div className="mp-todo-grid">
              {visible.map((t) => (
                <TodoCard
                  key={t.id}
                  todo={t}
                  isOpen={openId === t.id}
                  isEditing={editingId === t.id}
                  onToggleMemo={toggleMemo}
                  onAdvance={advance}
                  onStartEdit={startEdit}
                  onCancelEdit={cancelEdit}
                  onSaveEdit={saveEdit}
                  onDelete={doDelete}
                  onAddMemo={addMemo}
                  onDeleteMemo={deleteMemo}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default MypageHome;
